/**
 * @file Firestore helpers — thin wrappers around modular SDK calls so page
 * modules don't need to know collection names or query construction.
 *
 * Every watch* helper takes an optional trailing `onError` callback. Errors
 * used to be console.error-only, so a rules regression or outage left most
 * list pages showing an eternal skeleton with no user-visible signal; pages
 * now pass onError and render a "could not load" state (lib/load-error.js).
 *
 * Two read paths exist by design (see firestore.rules):
 *   • /employees, /devices       — admin only.
 *   • /employee_view, /device_view — sanitised mirrors, employees can read
 *                                    their own row.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, onSnapshot, serverTimestamp,
  writeBatch, runTransaction,
} from 'firebase/firestore';

import { db } from './firebase.js';
import { testCollectionName, isTestMode } from './test-mode.js';
import { normaliseSnForMatch } from './lib/sn-match.js';

// Test-mode-aware collection/doc resolvers. In production these are
// identity (`tcol('devices')` === `collection(db,'devices')`); in test
// mode they prepend the shared sandbox prefix (test_devices).
// EVERY collection reference in this file goes through these so the whole
// data layer flips namespaces from a single switch. The sole exception is
// `admins`: admin identity is global (you are the same admin in both
// modes), so it is never namespaced.
const tcol = (name) => collection(db, testCollectionName(name));
const tdoc = (name, ...path) => doc(db, testCollectionName(name), ...path);

// Composite-index fallback for test mode. Per-user sandbox collections
// (test_*) are separate collections, so the composite indexes production
// relies on (e.g. employees[phase,startDate]) can't be predefined for
// them — a where()+orderBy() query would throw "needs index". The test
// dataset is tiny, so in test mode we OMIT the orderBy from the query and
// sort the results in memory instead. Production keeps the indexed query
// unchanged.
//   orderClause(field, dir)  → [] in test mode, else [orderBy(field,dir)]
//   testSort(rows, field, dir) → sorts rows in memory iff in test mode
function orderClause(field, dir = 'asc') {
  return isTestMode() ? [] : [orderBy(field, dir)];
}
function toMillisish(v) {
  if (v == null) return 0;
  if (typeof v?.toMillis === 'function') return v.toMillis(); // Firestore Timestamp
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const t = Date.parse(v); // ISO string
  return Number.isNaN(t) ? 0 : t;
}
function testSort(rows, field, dir = 'asc') {
  if (!isTestMode()) return rows;
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => (toMillisish(a[field]) - toMillisish(b[field])) * sign);
}

export const cols = {
  employees:        () => tcol('employees'),
  employeeView:     () => tcol('employee_view'),
  devices:          () => tcol('devices'),
  deviceView:       () => tcol('device_view'),
  onboardings:      () => tcol('onboardings'),
  offboardings:     () => tcol('offboardings'),
  handovers:        () => tcol('handovers'),
  config:           () => tcol('config'),
  formSubmissions:  () => tcol('form_submissions'),
  admins:           () => collection(db, 'admins'), // never namespaced — global identity
  pendingChanges:   () => tcol('pending_changes'),
};

// ── Pending Abra changes ──────────────────────────────────────────────────
/**
 * Live stream of pending_changes. Caller can filter on status
 * ('pending' | 'reconciled' | 'cancelled') or deviceId. Order: newest
 * first by `ts`. Without a status filter, returns everything — the
 * Pending tab needs that to show reconciled rows in their cooldown
 * window.
 */
export function watchPendingChanges(filters, cb, onError) {
  const clauses = [];
  if (filters?.status)   clauses.push(where('status',   '==', filters.status));
  if (filters?.deviceId) clauses.push(where('deviceId', '==', filters.deviceId));
  const q = query(cols.pendingChanges(), ...clauses, ...orderClause('ts', 'desc'), limit(500));
  return onSnapshot(
    q,
    (snap) => cb(testSort(snap.docs.map((d) => ({ id: d.id, ...d.data() })), 'ts', 'desc')),
    (err)  => { console.error('[db] watchPendingChanges', err, filters); onError?.(err); },
  );
}

// ── Admins (admin only — Settings page) ───────────────────────────────────
/**
 * Live list of all admins. Rule allows admins to read the full
 * collection; non-admins can only read their own row (used by auth.js's
 * live isAdmin listener — not this helper).
 */
export function watchAdmins(cb, onError) {
  return onSnapshot(
    cols.admins(),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err)  => { console.error('[db] watchAdmins', err); onError?.(err); },
  );
}

export async function addAdmin({ email, displayName = null, addedBy }) {
  const lower = String(email).toLowerCase();
  await setDoc(doc(db, 'admins', lower), {
    email:       lower,
    displayName,
    addedAt:     serverTimestamp(),
    addedBy,
  });
}

export async function removeAdmin(email) {
  const lower = String(email).toLowerCase();
  await deleteDoc(doc(db, 'admins', lower));
}

// ── Employees (admin) ──────────────────────────────────────────────────────
export function watchEmployees(phase, cb, onError) {
  const q = query(cols.employees(), where('phase', '==', phase), ...orderClause('startDate', 'asc'));
  return onSnapshot(
    q,
    (snap) => cb(testSort(snap.docs.map((d) => ({ id: d.id, ...d.data() })), 'startDate', 'asc')),
    (err)  => { console.error('[db] watchEmployees', err); onError?.(err); },
  );
}

export async function getEmployee(id) {
  const snap = await getDoc(tdoc('employees', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createEmployee(data) {
  const ref = await addDoc(cols.employees(), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

// ── Employee view (any @strv user — reads own doc) ────────────────────────
export async function findEmployeeViewByEmail(email) {
  if (!email) return null;
  const q = query(cols.employeeView(), where('email', '==', email.toLowerCase()), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

// ── Devices (admin) ───────────────────────────────────────────────────────
/**
 * @param filters {{
 *   currentHolder?: string|null,    // employeeId, or null to match unassigned
 *   status?: string,
 *   category?: string,
 *   source?: 'flexi'|'gear',
 *   contextTag?: string,            // array-contains
 *   holderUnresolved?: boolean,
 * }}
 */
export function watchDevices(filters, cb, onError) {
  const clauses = [];
  if (filters.currentHolder === null)    clauses.push(where('currentHolder', '==', null));
  else if (filters.currentHolder)        clauses.push(where('currentHolder', '==', filters.currentHolder));
  if (filters.status)                    clauses.push(where('status',   '==', filters.status));
  if (filters.category)                  clauses.push(where('category', '==', filters.category));
  if (filters.source)                    clauses.push(where('source',   '==', filters.source));
  if (filters.contextTag)                clauses.push(where('contextTags', 'array-contains', filters.contextTag));
  if (filters.holderUnresolved === true) clauses.push(where('holderUnresolved', '==', true));
  const q = query(cols.devices(), ...clauses);
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err)  => { console.error('[db] watchDevices', err, filters); onError?.(err); },
  );
}

/**
 * Watch devices held by an employee — the materialise step writes
 * `allHolderIds` as the union of [Abra holder, Meraki holder] (deduped,
 * non-null). Using array-contains means a device with a Meraki/Abra holder
 * disagreement shows up on BOTH employees' /people pages with the mismatch
 * flag, exactly as the admin needs it.
 */
export function watchDevicesForHolder(holderId, cb, onError) {
  const q = query(cols.devices(), where('allHolderIds', 'array-contains', holderId));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err)  => { console.error('[db] watchDevicesForHolder', err, holderId); onError?.(err); },
  );
}

export async function getDevice(id) {
  const snap = await getDoc(tdoc('devices', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateDevice(id, patch) {
  await updateDoc(tdoc('devices', id), { ...patch, updatedAt: serverTimestamp() });
}

// ── Device view (any @strv user — reads devices currently held by them) ──
export function watchMyDeviceView(email, cb, onError) {
  if (!email) {
    cb([]);
    return () => {};
  }
  const q = query(cols.deviceView(), where('currentHolderEmail', '==', email.toLowerCase()));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err)  => { console.error('[db] watchMyDeviceView', err); onError?.(err); },
  );
}

// ── Onboardings ────────────────────────────────────────────────────────────

/**
 * Live-watch one /onboardings doc, firing with its `items` array ([] when the
 * doc doesn't exist). The person page renders the onboarding workflow card
 * from this snapshot, so an item action advances the row the moment it
 * commits (no stale-cache re-read).
 */
export function watchOnboarding(employeeId, cb, onError) {
  return onSnapshot(
    tdoc('onboardings', employeeId),
    (snap) => cb(snap.exists() ? (snap.data().items || []) : []),
    (err)  => { console.error('[db] watchOnboarding', err, employeeId); onError?.(err); },
  );
}

/**
 * Load every /onboardings doc once. Tiny collection (one per joiner),
 * cheap enough to fetch in full and join in memory with the employee list.
 */
export async function getAllOnboardings() {
  const snap = await getDocs(cols.onboardings());
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function setOnboardingItems(employeeId, items) {
  await setDoc(
    tdoc('onboardings', employeeId),
    { items, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function updateOnboardingItem(employeeId, itemId, patch) {
  const ref = tdoc('onboardings', employeeId);
  // Transaction, not read-then-write: two admins editing DIFFERENT items
  // on the same onboarding would otherwise last-write-wins the whole
  // `items` array and silently drop one set of edits. The transaction
  // re-reads inside the commit and retries on conflict.
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error(`onboarding ${employeeId} not found`);
    const items = (snap.data().items || []).map((it) =>
      it.id === itemId ? { ...it, ...patch } : it,
    );
    tx.update(ref, { items, updatedAt: serverTimestamp() });
  });
}

// ── Offboardings ───────────────────────────────────────────────────────────
export async function getOffboarding(employeeId) {
  const snap = await getDoc(tdoc('offboardings', employeeId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Update a single per-device decision in /offboardings/{employeeId}.
 * Replaces the older `setOffboardingDevices` model where the whole device
 * list lived embedded — devices now come from /devices live, only the
 * per-device choices live here.
 */
export async function setDeviceDecision(employeeId, deviceId, patch, actor) {
  const ref = tdoc('offboardings', employeeId);
  await setDoc(
    ref,
    {
      deviceDecisions: {
        [deviceId]: {
          ...patch,
          decidedAt: serverTimestamp(),
          decidedBy: actor || null,
        },
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Per-asset decision state — Bamboo-asset-id keyed (e.g. Sim Card rows).
 * Lives alongside deviceDecisions on /offboardings; the asset itself
 * comes from /employees.assets (synced from Bamboo's customAssets).
 */
export async function setAssetDecision(employeeId, bambooAssetId, patch, actor) {
  const ref = tdoc('offboardings', employeeId);
  await setDoc(
    ref,
    {
      assetDecisions: {
        [bambooAssetId]: {
          ...patch,
          decidedAt: serverTimestamp(),
          decidedBy: actor || null,
        },
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

// ── Handovers ──────────────────────────────────────────────────────────────
export async function recordHandover(record) {
  const ref = await addDoc(cols.handovers(), { ...record, ts: serverTimestamp() });
  return ref.id;
}

export async function getHandoversForEmployee(employeeId) {
  const q = query(
    cols.handovers(),
    where('employeeId', '==', employeeId),
    ...orderClause('ts', 'desc'),
  );
  const snap = await getDocs(q);
  return testSort(snap.docs.map((d) => ({ id: d.id, ...d.data() })), 'ts', 'desc');
}

/**
 * Device history: match by deviceId OR serial. Abra-owned onboarding
 * handovers are written BEFORE the device exists in Gear (deviceId null —
 * Finance creates the asset in Abra afterwards), so the SN — with the Abra
 * leading-'S' flex — is the durable join key for those rows. Results are
 * merged, deduped, and sorted in memory (a device has at most dozens of
 * rows), which also keeps the sn-variant query free of composite indexes.
 */
export async function getHandoversForDevice({ id, sn = null }) {
  const queries = [getDocs(query(cols.handovers(), where('deviceId', '==', id)))];
  if (sn) {
    const raw = String(sn).trim();
    const stripped = normaliseSnForMatch(raw); // uppercase, leading 'S' removed
    const variants = [...new Set([raw, raw.toUpperCase(), stripped, `S${stripped}`].filter(Boolean))];
    queries.push(getDocs(query(cols.handovers(), where('sn', 'in', variants))));
  }
  const snaps = await Promise.all(queries);
  const byId = new Map();
  for (const s of snaps) {
    for (const d of s.docs) byId.set(d.id, { id: d.id, ...d.data() });
  }
  return [...byId.values()].sort((a, b) => toMillisish(b.ts) - toMillisish(a.ts));
}

// ── Config ─────────────────────────────────────────────────────────────────
export async function getConfig(docId) {
  const snap = await getDoc(tdoc('config', docId));
  return snap.exists() ? snap.data() : null;
}

export function watchConfig(docId, cb) {
  return onSnapshot(tdoc('config', docId), (snap) => cb(snap.exists() ? snap.data() : null));
}

export async function setConfig(docId, data) {
  await setDoc(tdoc('config', docId), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

// ── Form submissions ───────────────────────────────────────────────────────
/**
 * Live query of pending form submissions. Filters out ignored rows. The
 * "matchStatus" filter is optional — pass 'unmatched' to surface only the
 * queue items needing admin action.
 */
export function watchFormSubmissions({ matchStatus } = {}, cb) {
  const clauses = [];
  if (matchStatus) clauses.push(where('matchStatus', '==', matchStatus));
  const q = query(cols.formSubmissions(), ...clauses, ...orderClause('timestamp', 'desc'));
  return onSnapshot(
    q,
    (snap) => cb(testSort(snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((s) => !s.ignoredAt), 'timestamp', 'desc')),
    (err) => console.error('[db] watchFormSubmissions', err),
  );
}

export async function linkFormSubmission({ submissionId, employeeId, employeeName, items, actorEmail }) {
  // Both writes commit atomically. Previously they were two sequential
  // setDocs: a failure between them could leave a submission marked
  // matched with no onboarding doc (or vice versa). A batch removes that
  // partial-failure window.
  const batch = writeBatch(db);
  batch.set(tdoc('form_submissions', submissionId), {
    matchStatus:         'matched',
    matchedEmployeeId:   employeeId,
    matchedEmployeeName: employeeName,
    matchedBy:           'manual',
    matchedAt:           serverTimestamp(),
    matchedByActor:      actorEmail || null,
  }, { merge: true });
  batch.set(tdoc('onboardings', employeeId), {
    items,
    formSubmissionId: submissionId,
    updatedAt:        serverTimestamp(),
  }, { merge: true });
  await batch.commit();
}

export async function ignoreFormSubmission(submissionId, actorEmail) {
  await setDoc(tdoc('form_submissions', submissionId), {
    ignoredAt: serverTimestamp(),
    ignoredBy: actorEmail || null,
  }, { merge: true });
}

